import { FC, useMemo, useState } from 'react';
import { styled } from '@mui/material/styles';
import { Button } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import format from 'date-fns/format';

import Modal from 'src/presentations/components/molecules/modal';

import { beautifyEnum, capitalizeName } from 'src/utils/utility';
import IconCollapse from 'src/assets/images/icons/grid-2.svg';
import IconExpand from 'src/assets/images/icons/list.svg';
import TransportMediumIcon from '../card/TransportMediumIcon';

import { useNotificationSelector } from './NotificationContext';
import { INotificationDetailProps } from './types';
import { NotificationDetailInvoiceFiles } from './detail/InvoiceFiles';

const StyledModal = styled(Modal)(({ theme }) => ({
  '& .MuiDialog-container': {
    zIndex: 990,
  },
  '& .MuiDialog-paper': {
    maxWidth: '1000px',
    borderRadius: '16px',
    height: 'calc(100% - 64px)',
    zIndex: 999,

    '& .MuiDialogContent-root': {
      padding: '20px 0',
    },
  },
}));

export const NotificationDetail: FC<INotificationDetailProps> = ({ errorInvoiceFileProcessingStatuses }) => {
  const [isCollapseAll, setIsCollapseAll] = useState(false);
  const [isExpandAll, setIsExpandAll] = useState(true); // Default to expanded
  const { selectedDetail, setSelectedDetail, setIsShowDetail, isShowDetail } = useNotificationSelector();

  // Determine the current expanded state - expand takes priority over collapse
  const defaultExpanded = useMemo(() => {
    if (isExpandAll) return true;
    if (isCollapseAll) return false;
    return true; // Default fallback
  }, [isExpandAll, isCollapseAll]);

  const invoiceProcessed = useMemo(
    () =>
      selectedDetail?.notificationStatus === 'EMAIL_RECEIVED'
        ? selectedDetail?.processingInvoices || 0
        : (selectedDetail?.flaggedInvoices || 0) +
          (selectedDetail?.errorInvoices || 0) +
          (selectedDetail?.rejectedInvoices || 0) +
          (selectedDetail?.approvedInvoices || 0),
    [selectedDetail]
  );

  const onCloseModal = () => {
    setIsShowDetail(false);
    // Keep selectedDetail data for when user re-opens the modal
  };

  if (!selectedDetail || !isShowDetail) {
    return null;
  }

  return (
    <StyledModal open={isShowDetail} onClose={onCloseModal} isCloseOutsideClicked fullWidth maxWidth='md'>
      <div className='h-full flex flex-col'>
        <div className='px-24 pb-24 border-b border-neutral-20 flex'>
          <div className='flex-1'>
            {selectedDetail?.notificationStatus === 'EMAIL_RECEIVED' && !selectedDetail?.serviceProviderCodes?.length ? (
              <div className='w-[162px] h-[24px] rounded-[3px] bg-[#F5F6FA] my-4' />
            ) : (
              <div className='flex items-center gap-10'>
                <div className='flex items-center justify-center'>
                  {selectedDetail?.modalities?.map((item, index) => (
                    <div key={`notif-${selectedDetail?.notificationId}-modality-${item}`} className={index > 0 ? '-ml-10' : ''}>
                      <TransportMediumIcon medium={item} iconColor='#9F9F9F' />
                    </div>
                  ))}
                </div>
                <h2 className='text-18 xl:text-20 font-bold text-blue_grey-80 max-lg:text-18'>
                  {selectedDetail?.serviceProviderCodes?.length > 1 ? (
                    <>{selectedDetail?.serviceProviderCodes?.length} SERVICE PROVIDERS</>
                  ) : (
                    <>{beautifyEnum(selectedDetail?.serviceProviderCodes[0])?.toUpperCase()}</>
                  )}
                </h2>
                {selectedDetail?.modalities.length > 0 && (
                  <>
                    <div className='w-4 h-4 rounded-full bg-[#D9D9D9]' />
                    <div className='uppercase text-neutral-60 text-14 xl:text-16'>{selectedDetail?.modalities?.join(' & ')}</div>
                  </>
                )}
              </div>
            )}
            <div className='flex items-center gap-12 mt-12'>
              <div className='flex flex-col'>
                <h3 className='text-16 xl:text-18 font-bold'>Current Invoice Statuses in System: {invoiceProcessed} Items</h3>
                <p className='text-12 xl:text-14 text-blue_grey-80 font-medium mt-0 xl:mt-5'>
                  {selectedDetail?.notificationSourceType === 'EMAIL' ? 'Email received on' : 'Uploaded on'}{' '}
                  {format(new Date(selectedDetail?.uploadedOn), "dd-MMM-yyyy 'at' hh:mm a")}{' '}
                  {selectedDetail?.notificationSourceType === 'EMAIL' ? 'from' : 'by'}{' '}
                  <strong className='font-medium text-neutral-80'>
                    {selectedDetail?.notificationSourceType === 'EMAIL'
                      ? selectedDetail?.uploadedBy // Show name as is for EMAIL
                      : capitalizeName(selectedDetail?.uploadedBy)}{' '}
                    {/* Apply the utility for other cases */}
                  </strong>
                </p>
              </div>
              <div className='ml-auto flex items-center gap-16'>
                <Button
                  type='button'
                  variant='outlined'
                  color='inherit'
                  onClick={() => {
                    setIsCollapseAll(true);
                    setIsExpandAll(false);
                  }}
                  sx={{ textTransform: 'capitalize', borderColor: '#dfdfdf', backgroundColor: '#fff', gap: '12px' }}
                >
                  Collapse All
                  <img src={IconCollapse} alt='' />
                </Button>
                <Button
                  type='button'
                  variant='outlined'
                  color='inherit'
                  onClick={() => {
                    setIsExpandAll(true);
                    setIsCollapseAll(false);
                  }}
                  sx={{ textTransform: 'capitalize', borderColor: '#dfdfdf', backgroundColor: '#fff', gap: '12px' }}
                >
                  Expand All
                  <img src={IconExpand} alt='' />
                </Button>
              </div>
            </div>
          </div>
          <div className='ml-auto pt-6'>
            <button type='button' aria-label='Close' onClick={onCloseModal}>
              <CloseIcon className='text-neutral-60 text-30' />
            </button>
          </div>
        </div>
        <NotificationDetailInvoiceFiles errorInvoiceFileProcessingStatuses={errorInvoiceFileProcessingStatuses} defaultExpanded={defaultExpanded} />
        <div className='pt-24 px-24 border-t border-neutral-20 flex items-center justify-end'>
          <Button
            type='button'
            variant='outlined'
            color='inherit'
            // disabled={!batchId || batchId === ''}
            onClick={() => {
              setIsShowDetail(false);
              setSelectedDetail(null);
            }}
            sx={{ textTransform: 'capitalize', borderColor: '#dfdfdf' }}
          >
            Close
          </Button>
        </div>
      </div>
    </StyledModal>
  );
};
